import { useEffect, useState } from "react";
import { filter, findLast, map } from "lodash";
import useNotificationActions from "modules/notification/hooks/useNotificationActions";
import { useI18n } from "i18n";
import { Container } from "@remo-co/ui-core/src/components/Container";
import { Box } from "@remo-co/ui-core/src/components/Box";
import { Paper } from "@remo-co/ui-core/src/components/Paper";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { CopyInput } from "@remo-co/ui-core/src/components/CopyInput";
import { Button } from "@remo-co/ui-core/src/components/Button";
import { Link } from "@remo-co/ui-core/src/components/Link";
import RemoPNG from "modules/appToken/assets/remo.png";
import ZapierPNG from "modules/appToken/assets/zapier.png";
import PabblyPNG from "modules/appToken/assets/pabbly.png";
import { useCompanyPlanSettings } from "modules/companyPlanSettings";
import { useAppDispatch } from "store/hooks";
import { addDialogNotification } from "modules/dialogNotification/redux/dialogNotificationSlice";
import useAppTokens from "./hooks/useAppTokens";
import { useStyles, styles } from "./styles";
import { IAuthToken } from "./appToken.api";
import { tokenKinds } from "./constants";

const AppToken = () => {
  const [appTokens, setAppTokens] = useState<IAuthToken[]>();
  const [selectedKind, setSelectedKind] = useState<string>();
  const dispatch = useAppDispatch();
  const appTokenHook = useAppTokens();
  const { t } = useI18n(["common", "eventForm", "settings"]);
  const { addSuccessNotification, addErrorNotification } =
    useNotificationActions();
  const { areIntegrationsEnabled } = useCompanyPlanSettings();
  const classes = useStyles();

  const handleCopied = () => {
    addSuccessNotification({
      message: "Copied the token",
      autoDismiss: 5,
    });
  };

  const getTokenByKind = (kind: string) =>
    findLast(appTokens, (appToken) => appToken.kind === kind);

  const getTokenStringByKind = (kind: string) => {
    const token = getTokenByKind(kind);

    return token ? token.token : "";
  };

  const generateNewToken = async (kind?: string) => {
    try {
      const tokenData = await appTokenHook.generateAppToken(
        (kind || selectedKind) ?? "",
      );

      const oldTokens = filter(
        appTokens,
        (token) => token.kind !== tokenData.kind,
      );

      setAppTokens([tokenData, ...oldTokens]);
    } catch (error) {
      addErrorNotification({
        message: t("settings:generating.token.error"),
      });
    }
  };

  const handleConfirmation = () => {
    generateNewToken();
  };

  const handleGenerateToken = (kind: string) => {
    setSelectedKind(kind);

    if (getTokenByKind(kind)) {
      dispatch(
        addDialogNotification({
          confirmText: t("button.generate"),
          dismissText: t("button.close"),
          onConfirm: handleConfirmation,
          message: t("dialog.message.token"),
          title: t("dialog.token.title"),
          closeIconStyles: styles.closeIcon,
          contentStyles: styles.content,
          confirmBtnStyles: styles.confirmButton,
          dismissBtnStyles: styles.cancelButton,
        }),
      );
    } else {
      generateNewToken(kind);
    }
  };

  useEffect(() => {
    const setTokens = async () => {
      const tokens = await appTokenHook.getAppTokenList();
      setAppTokens(tokens.tokens);
    };

    setTokens();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <Container>
        <Paper elevation={0}>
          <Typography variant="h3" className={classes.title}>
            {t("settings:app.token")}
          </Typography>
          <div className={classes.description}>
            <Typography variant="h5" className={classes.descriptionHeading}>
              {t("settings:external.api.token.description")}
            </Typography>

            {map(tokenKinds, (tokenKind) => (
              <div className={classes.inputContainer} key={tokenKind.kind}>
                <CopyInput
                  label={t("button.copy")}
                  text={getTokenStringByKind(tokenKind.kind)}
                  onCopy={handleCopied}
                />
                <Button
                  variant="primary"
                  color="blue"
                  onClick={() => handleGenerateToken(tokenKind.kind)}
                  size="sm"
                  disabled={!areIntegrationsEnabled}
                  data-testid="generate-button"
                  className={classes.generateButton}
                >
                  {t("eventForm:generate")}
                </Button>
              </div>
            ))}

            <Typography variant="h5" className={classes.descriptionHeading}>
              {t("settings:external.api.token.description.2")}
            </Typography>
          </div>
          <Box display="flex" className={classes.apps}>
            <Paper elevation={0} className={classes.app}>
              <img
                alt={t("eventForm:zapier")}
                className={classes.appIcon}
                src={ZapierPNG}
              />
              <div>
                <Typography variant="h5" className={classes.appName}>
                  {t("eventForm:zapier")}
                </Typography>
                <Typography variant="body1">
                  {t("eventForm:zapier.description")}
                </Typography>
                <Link
                  openInNewTab
                  href={t("url:zapier.help.link")}
                  data-testid="zapier-help-link"
                  aria-label={t("eventForm:zapier.how.to.use")}
                >
                  <Typography
                    variant="caption"
                    component="span"
                    underline
                    className={classes.helpLink}
                  >
                    {t("eventForm:zapier.how.to.use")}
                  </Typography>
                </Link>
              </div>
            </Paper>
            <Paper elevation={0} className={classes.app}>
              <img
                alt={t("settings:pabbly.connect")}
                className={classes.appIcon}
                src={PabblyPNG}
              />
              <div>
                <Typography variant="h5" className={classes.appName}>
                  {t("settings:pabbly.connect")}
                </Typography>
                <Typography variant="body1">
                  {t("settings:pabbly.connect.description")}
                </Typography>
                <Link
                  openInNewTab
                  href={t("url:pabbly.connect.view.docs")}
                  data-testid="pabbly-connect-help-link"
                  aria-label={t("settings:pabbly.connect.how.to.use")}
                >
                  <Typography
                    variant="caption"
                    component="span"
                    underline
                    className={classes.helpLink}
                  >
                    {t("settings:pabbly.connect.how.to.use")}
                  </Typography>
                </Link>
              </div>
            </Paper>
          </Box>
          <Box display="flex" className={classes.apps}>
            <Paper elevation={0} className={classes.app}>
              <img
                alt={t("settings:remo.external.api")}
                className={classes.appIcon}
                src={RemoPNG}
              />
              <div>
                <Typography variant="h5" className={classes.appName}>
                  {t("settings:remo.external.api")}
                </Typography>
                <Typography variant="body1">
                  {t("settings:remo.external.api.description")}
                </Typography>
                <Link
                  openInNewTab
                  href={t("url:external.api")}
                  data-testid="remo-api-help-link"
                  aria-label={t("settings:remo.external.api.view.docs")}
                >
                  <Typography
                    variant="caption"
                    component="span"
                    underline
                    className={classes.helpLink}
                  >
                    {t("settings:remo.external.api.view.docs")}
                  </Typography>
                </Link>
              </div>
            </Paper>
          </Box>
        </Paper>
      </Container>
    </div>
  );
};

export default AppToken;
